import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateAdProvider, useCreateAdContext } from '../../context/CreateAdContext';
import CreateAd from './CreateAd';
import AiChatBot from '../AiChatBot/AiChatBot';

// En-tête partagé (titre + bascule Manual/IA + bascule Simple/Advanced) rendu
// UNE FOIS au-dessus des deux colonnes plutôt que dans CreateAd — avant ce
// correctif, CreateAd affichait son propre en-tête (titre/bascules/hr) DANS
// la colonne formulaire, tandis que le panneau de chat commençait directement
// en haut de la sienne : les deux contenus démarraient à des hauteurs
// différentes ("le chatbot ne colle pas aux saisies"). isSimpleCampaign vit
// maintenant dans CreateAdContext pour que ce header partagé et CreateAd (qui
// passe hideHeader) restent synchronisés sur le même état.
const SharedHeader = () => {
  const navigate = useNavigate();
  const { isSimpleCampaign, setIsSimpleCampaign } = useCreateAdContext();
  return (
    <div className="mb-4">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center w-full gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#221C51]">Create Your Ad with AI</h2>
          <p className="text-xs text-gray-500 mt-0.5">L'assistant à droite remplit le formulaire au fil de la conversation.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center bg-gray-100 rounded-full p-0.5 text-xs font-semibold">
            <button type="button" onClick={() => navigate('/dashboard/create-ad')}
              className="px-3 py-1 rounded-full transition-all duration-200 text-gray-500 hover:text-[#221C51]">
              Manual
            </button>
            <button type="button" className="px-3 py-1 rounded-full transition-all duration-200 bg-[#221C51] text-white shadow">
              Use AI
            </button>
          </div>
          <p className="text-sm text-[#221C51] mr-2 font-semibold">{isSimpleCampaign ? 'Simple' : 'Advanced'}</p>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={!isSimpleCampaign} onChange={() => setIsSimpleCampaign(!isSimpleCampaign)} className="sr-only peer" />
            <div className="w-12 h-6 bg-[#221C51] rounded-full">
              <div className="w-6 h-6 bg-white rounded-full shadow-md transform peer-checked:translate-x-6 transition-transform duration-200" />
            </div>
          </label>
        </div>
      </div>
      <hr className="border-t border-[#221C51]/30 mt-3" />
    </div>
  );
};

// En-dessous de xl, Chat et Formulaire ne peuvent pas tenir côte à côte
// (colonnes trop étroites) — avant ce correctif, les deux étaient empilés
// verticalement avec le chat forcé en haut sur 70vh, obligeant à scroller
// une pleine hauteur d'écran de conversation avant même d'apercevoir le
// formulaire. À la place : un vrai bascule Chat/Formulaire façon onglets,
// avec bascule AUTOMATIQUE vers "Formulaire" ~1s après qu'Alex ait rempli
// des champs, pour que l'utilisateur voie le résultat sans action manuelle.
// À partir de xl: les deux colonnes redeviennent visibles simultanément et
// les onglets disparaissent (comportement desktop inchangé).
const MobileTabs = ({ tab, setTab, justFilled }) => (
  <div className="xl:hidden join w-full mb-4">
    <button type="button" onClick={() => setTab('chat')}
      className={`join-item btn btn-sm flex-1 font-semibold border-[#221C51]/20 ${tab === 'chat' ? 'bg-[#221C51] hover:bg-[#221C51] text-white border-[#221C51]' : 'bg-white text-[#221C51]'}`}>
      💬 Alex AI
    </button>
    <button type="button" onClick={() => setTab('form')}
      className={`join-item btn btn-sm flex-1 font-semibold border-[#221C51]/20 relative ${tab === 'form' ? 'bg-[#221C51] hover:bg-[#221C51] text-white border-[#221C51]' : 'bg-white text-[#221C51]'}`}>
      📝 Formulaire
      {justFilled && tab !== 'form' && (
        <span className="absolute top-0.5 right-1.5 w-2 h-2 rounded-full bg-green-400 animate-pulse" />
      )}
    </button>
  </div>
);

const CreateAdWithAIInner = ({ isPremium }) => {
  const [mobileTab, setMobileTab] = useState('chat');
  const [justFilled, setJustFilled] = useState(false);
  const { pendingActions } = useCreateAdContext();

  // Dès qu'Alex remplit des champs pendant que l'utilisateur discute (mobile/
  // tablette), on bascule automatiquement vers l'onglet Formulaire après un
  // court délai — le temps de lire la réponse d'Alex et ses puces de
  // confirmation — pour montrer le résultat sans que l'utilisateur ait à
  // chercher l'onglet lui-même.
  useEffect(() => {
    if (!pendingActions || pendingActions.length === 0) return;
    setJustFilled(true);
    if (mobileTab === 'chat') {
      const t = setTimeout(() => setMobileTab('form'), 1100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingActions]);

  // Le badge "nouveaux champs" ne sert que tant que l'utilisateur n'a pas
  // encore regardé le formulaire — il se dissipe dès qu'il l'ouvre, peu
  // importe si c'est via le clic manuel ou la bascule automatique ci-dessus.
  useEffect(() => {
    if (mobileTab === 'form') setJustFilled(false);
  }, [mobileTab]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto">
      <SharedHeader />
      <MobileTabs tab={mobileTab} setTab={setMobileTab} justFilled={justFilled} />
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-6 items-start">
        <div className={`min-w-0 ${mobileTab === 'form' ? 'block' : 'hidden'} xl:block`}>
          <CreateAd aiMode isPremium={isPremium} hideHeader />
        </div>
        <div className={`xl:sticky xl:top-6 h-[68vh] xl:h-[calc(100vh-140px)] ${mobileTab === 'chat' ? 'block' : 'hidden'} xl:block`}>
          <AiChatBot embedded />
        </div>
      </div>
    </div>
  );
};

const CreateAdWithAI = ({ isPremium }) => (
  <CreateAdProvider>
    <CreateAdWithAIInner isPremium={isPremium} />
  </CreateAdProvider>
);

export default CreateAdWithAI;
